/* eslint-disable webflow/module-top-level-imports */

// HACK: Manually tree-shake here just what we need to reduce startup time in
// ESLint plugin:
import {matchExpression} from '@packages/systems/wfdl/matchExpression';
import {matchType} from '@packages/systems/wfdl/matchType';
import {
  getSystem,
  getMeta,
  type Type,
} from '@packages/systems/wfdl/DataTypeDefinition';
import * as Expressions from '@packages/systems/wfdl/Expressions';

import * as Types from '@packages/systems/wfdl/Types';

/* eslint-enable webflow/module-top-level-imports */

import {print} from '@packages/systems/wfdl/codegen/wfdl';
import {toHumanReadableString} from '@packages/systems/wfdl-Qualified';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  constant,
  constantTrue,
  constantFalse,
} from '@packages/utilities/fp/utils';

import {getExpressionFromAny} from '@packages/systems/wfdl-ast';
import {getTypeFromAny} from '@packages/systems/wfdl';

type JSONMLAttributes = {
  [key: string]: [string];
};
type JSONMLElement = [string, JSONMLAttributes, JSONML];
type JSONMLObjectReference = [
  'object',
  {
    object: any;
  },
];
type JSONML = string | JSONMLElement | JSONMLObjectReference;

// @ts-expect-error - TS7006 - Parameter 'object' implicitly has an 'any' type.
const reference = (object): JSONML => {
  return ['object', {object}];
};

const preview = (object: Expression): JSONML => {
  // @ts-expect-error - TS2322 - Type '{ isPreview: true; }' is not assignable to type '[string]'.
  return ['object', {object, config: {isPreview: true}}];
};

function div(attrs = {}, children: Array<JSONML>): JSONML {
  // @ts-expect-error - TS2322 - Type '["div", {}, ...JSONML[]]' is not assignable to type 'JSONML'.
  return ['div', attrs, ...children];
}

function span(attrs = {}, children: Array<JSONML>): JSONML {
  // @ts-expect-error - TS2322 - Type '["span", {}, ...JSONML[]]' is not assignable to type 'JSONML'.
  return ['span', attrs, ...children];
}

function ol(attrs = {}, children: Array<JSONML>): JSONML {
  // @ts-expect-error - TS2322 - Type '["ol", {}, ...JSONML[]]' is not assignable to type 'JSONML'.
  return ['ol', attrs, ...children];
}

function li(attrs = {}, children: Array<JSONML>): JSONML {
  // @ts-expect-error - TS2322 - Type '["li", {}, ...JSONML[]]' is not assignable to type 'JSONML'.
  return ['li', attrs, ...children];
}

export const getBodyForExpression: (arg1: Expression) => JSONML | null =
  matchExpression({
    Literal: (val) => reference(val.value),
    Record: (val) =>
      ol(
        listStyle,
        Object.keys(val).map((key) =>
          li({}, [
            span(keyStyle, [key]),
            span({}, [': ']),
            span({}, [reference(val[key])]),
          ])
        )
      ),
    Case: (val) =>
      ol(
        listStyle,
        Object.keys(val.cases).map((key) =>
          li({}, [
            span(keyStyle, [key]),
            span({}, [': ']),
            span({}, [reference(val.cases[key])]),
          ])
        )
      ),
    List: (val) =>
      ol(
        listStyle,
        val.map((item) => li({}, [reference(item)]))
      ),
    Element: (val) => {
      return ol(listStyle, [
        li({}, [reference(Expressions.getElementData(val))]),
      ]);
    },
    Union: (val) => ol(listStyle, [li({}, [reference(val.value)])]),
    Call: (val) =>
      ol(listStyle, [
        li({}, [span(keyStyle, ['fun']), span({}, [': ']), reference(val.fun)]),
        li({}, [span(keyStyle, ['arg']), span({}, [': ']), reference(val.arg)]),
      ]),
    Typed: (val) =>
      ol(listStyle, [
        li({}, [
          span(keyStyle, ['type']),
          span({}, [': ']),
          reference(val.type),
        ]),
        li({}, [
          span(keyStyle, ['expr']),
          span({}, [': ']),
          reference(val.expr),
        ]),
      ]),
    Distinct: (val) =>
      ol(listStyle, [
        li({}, [
          span(keyStyle, ['name']),
          span({}, [': ']),
          span(inlineValuesStyle, [`#${toHumanReadableString(val.name)}`]),
        ]),
        li({}, [
          span(keyStyle, ['value']),
          span({}, [': ']),
          reference(val.value),
        ]),
      ]),
    _: (_) => null,
  });

const expressionNameStyle = {
  style: 'color:rgb(59, 121, 195); position:relative;',
} as const;
const inlineValuesStyle = {
  style: 'color: #777; font-style: italic; position:relative;',
} as const;
const nullStyle = {style: 'color: #777; position:relative;'} as const;
const valueStyle = {
  style: 'color: #881391; font-weight: bolder; position:relative;',
} as const;
const listStyle = {
  style:
    'list-style-type: none; padding: 0; margin: 0 0 0 12px; font-style: normal; position:relative;',
} as const;
const keyStyle = {style: 'color: #881391; position:relative;'} as const;

const getRecordPreview: (arg1: Expression) => Array<JSONML> = matchExpression({
  Record: (val) => {
    const fields = Object.keys(val);
    const fieldPreviews = fields.map((field) =>
      span({}, [
        span(inlineValuesStyle, [' ']),
        span(inlineValuesStyle, [field]),
        span(inlineValuesStyle, ['={']),
        // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'Expression'.
        span(inlineValuesStyle, [preview(val[field])]),
        span(inlineValuesStyle, ['}']),
      ])
    );
    return [span({}, fieldPreviews)];
  },
  _: constant([]),
});

const getHeaderForExpression: (
  arg1: Expression
) => (arg1: Expression) => JSONML = matchExpression({
  Boolean: (val) => constant(reference(val)),
  Number: (val) => constant(reference(val)),
  Text: (val) => constant(reference(val)),
  Enum: constant((expression) => span(inlineValuesStyle, [print(expression)])),
  Variable: constant((expression) => span(valueStyle, [print(expression)])),
  Select: constant((expression) => span(valueStyle, [print(expression)])),
  // Hole Emoji - String.fromCodePoint(0x1f573)
  Hole: constant((expression) => span(nullStyle, [print(expression)])),
  Literal: (val) =>
    constant(
      div({}, [
        span(expressionNameStyle, ['Literal ']),
        span(inlineValuesStyle, [`#${toHumanReadableString(val.name)}`]),
      ])
    ),
  Record: constant(constant(span(expressionNameStyle, ['Record']))),
  List: constant(constant(span(expressionNameStyle, ['List']))),
  Element: (val) => (_: Expression) => {
    const data = Expressions.getElementData(val);
    return div({}, [
      span(expressionNameStyle, [`<${toHumanReadableString(val.type)} `]),
      span(inlineValuesStyle, [`#${val.id}`]),
      ...getRecordPreview(data),
      span(expressionNameStyle, [`>`]),
    ]);
  },
  Union: (val) =>
    constant(
      div({}, [
        span(expressionNameStyle, ['Union ']),
        span(inlineValuesStyle, [`#${val.tag}`]),
      ])
    ),
  Call: (val) =>
    constant(
      div({}, [
        span(expressionNameStyle, [preview(val.fun)]),
        span(expressionNameStyle, [`(`]),
        span(inlineValuesStyle, [preview(val.arg)]),
        span(expressionNameStyle, [`)`]),
      ])
    ),
  Function: (val) =>
    constant(
      div({}, [
        span(valueStyle, [val.param]),
        span(inlineValuesStyle, [` => `]),
        span(inlineValuesStyle, [reference(val.body)]),
      ])
    ),
  Case: (val) =>
    constant(
      span({}, [span(expressionNameStyle, ['Case']), reference(val.value)])
    ),
  Extend: (val) =>
    constant(
      div({}, [
        span(valueStyle, [reference(val.record)]),
        span(inlineValuesStyle, [` #${val.prop} -> `]),
        span(inlineValuesStyle, [reference(val.value)]),
      ])
    ),
  Typed: (val) =>
    constant(
      div({}, [
        span(inlineValuesStyle, [reference(val.expr)]),
        span({}, [' :: ']),
        span(valueStyle, [reference(val.type)]),
      ])
    ),
  Distinct: (val) =>
    constant(
      div({}, [
        span(expressionNameStyle, ['Distinct ']),
        span(inlineValuesStyle, [`#${toHumanReadableString(val.name)}`]),
        span(inlineValuesStyle, [' ']),
        span(inlineValuesStyle, [reference(val.value)]),
      ])
    ),
});

const isEmptyRecord = matchExpression({
  Record: (val) => Object.keys(val).length === 0,
  _: constantFalse,
});
const hasBodyForExpression = matchExpression({
  Element: constantTrue,
  Literal: constantTrue,
  Case: constantTrue,
  Call: constantTrue,
  Distinct: constantTrue,
  Union: (val) => {
    if (isEmptyRecord(val.value)) return false;
    return true;
  },
  Record: (val) => {
    if (Object.keys(val).length === 0) return false;
    return true;
  },
  List: (val) => {
    if (val.length === 0) return false;
    return true;
  },
  _: constantFalse,
});

const inlineTypeStyle = {
  style: 'color:#666; position:relative;',
} as const;

const getHeaderForType: (arg1: Type) => (arg1: Type) => JSONML | null =
  matchType({
    RowCons: (_) => (type: Type) => {
      const row = Types.deconstructRow(type);
      const labels = Object.keys(row.items);
      return div(
        {},
        labels.map((label) =>
          div({}, [
            span(keyStyle, [label]),
            span(inlineTypeStyle, [' :: ']),
            span(inlineTypeStyle, [reference(row.items[label])]),
          ])
        )
      );
    },
    TypeApplication: (val) => (type: Type) => {
      const name = Types.getTypeConstructor(val.con);
      if (name) {
        return span(inlineTypeStyle, [
          toHumanReadableString(name),
          '<',
          reference(val.arg),
          '>',
        ]);
      }
      const fun = Types.getFunctionArgAndRetTypes(type);
      if (fun) {
        return span(inlineTypeStyle, [
          reference(fun.arg),
          ' => ',
          reference(fun.ret),
        ]);
      }
      return null;
    },
    RowEmpty: () => () => span(inlineTypeStyle, ['Empty']),
    Element: () => () => span(inlineTypeStyle, ['Element']),
    TypeReference: (val) => () => span(inlineTypeStyle, [val]),
    TypeConstructor: (val) => (_: Type) =>
      span(inlineTypeStyle, [toHumanReadableString(val)]),
    Generic: (val) => (_: Type) =>
      span(inlineTypeStyle, ['<', val.arg, '>', reference(val.body)]),
    _: (val) => (_: Type) => reference(val),
  });

const hasBodyForType: (arg1: Type) => boolean = matchType({
  _: (val) => {
    if (getSystem(val) || getMeta(val)) return true;

    return false;
  },
});

const getBodyForType: (arg1: Type) => JSONML | null = matchType({
  _: (val) => {
    const system = getSystem(val);
    const meta = getMeta(val);
    return ol(listStyle, [
      ...(system
        ? [li({}, [span(keyStyle, ['system: ']), reference(getSystem(val))])]
        : []),
      ...(meta
        ? [li({}, [span(keyStyle, ['meta: ']), reference(getMeta(val))])]
        : []),
    ]);
  },
});

type Config = {
  isPreview: true;
};

export const formatter = {
  header: function (obj: any) {
    const expr = getExpressionFromAny(obj);
    if (expr) {
      return getHeaderForExpression(expr)(expr);
    }
    const type = getTypeFromAny(obj);
    if (type) {
      return getHeaderForType(type)(type);
    }
    return null;
  },
  hasBody: function (obj: any, config?: Config) {
    const expr = getExpressionFromAny(obj);
    if (expr) {
      if (config && config.isPreview) return false;
      return hasBodyForExpression(expr);
    }

    const type = getTypeFromAny(obj);
    if (type) {
      return hasBodyForType(type);
    }

    return null;
  },
  body: function (obj: any) {
    const expr = getExpressionFromAny(obj);
    if (expr) {
      return getBodyForExpression(expr);
    }
    const type = getTypeFromAny(obj);
    if (type) {
      return getBodyForType(type);
    }
    return null;
  },
} as const;
